import { createMutation, invalidateQueries } from '@amzn/gsce-shared-react-lib';
import {
  ReopenAuditCommand,
  type ReopenAuditCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import { apiHeliosClient, withRetry } from '../../../shared/data';
import { schedulePostMutationRefresh } from '../../../shared/hooks';
import { AuditQueryKeys } from './keys';
import { patchAuditSummaryCache } from '../logic/patchAuditSummaryCache';

/**
 * Mutation hook for reopening a completed/expired audit.
 * Requires new expiration date, reason, and who is reopening.
 *
 * On success, directly patches the cached audit summary with the
 * response data (avoids eventual consistency stale reads on refetch),
 * then invalidates all relevant caches and schedules follow-up
 * refreshes for ES eventual consistency.
 */
export const useReopenAuditMutation = createMutation(
  (input: ReopenAuditCommandInput) =>
    withRetry(() => apiHeliosClient.send(new ReopenAuditCommand(input))),
  {
    onSuccess: (queryClient, data, variables) => {
      patchAuditSummaryCache(queryClient, variables.auditId, data.audit);
      // Reopen resets the audit's answer state server-side. Invalidate
      // caches that were NOT patched above:
      //   - `lists`: the audit's status flipped from COMPLETED/EXPIRED
      //     back to IN_PROGRESS, and active list filters need to
      //     reflect that.
      //   - `details`: any separate detail query (not the summary).
      //   - `questionResponse`: single-question queries the perform
      //     page fetches on demand — answer state was reset server-side.
      //
      // `summary` is NOT invalidated here — we just patched it with
      // the response data. An immediate invalidation would trigger a
      // refetch that could return stale ES data and overwrite our
      // patch. The delayed refresh below reconciles eventually.
      void invalidateQueries(queryClient, [
        AuditQueryKeys.lists,
        AuditQueryKeys.details,
        AuditQueryKeys.questionResponse,
      ]);
      // ES eventual consistency: status changes may not reflect
      // immediately in backend reads. Schedule follow-up refreshes
      // that reconcile the summary + list caches once ES catches up.
      schedulePostMutationRefresh(queryClient, {
        queryKeys: [AuditQueryKeys.lists, AuditQueryKeys.summary, AuditQueryKeys.details],
      });
    },
  },
);
